import { useEffect, useState } from 'react';
import { enregistreFichier, fichierDocumentDemande, ouvreDocument } from '../../api/documents';
import { envoisApi } from '../../api/resources';
import { FRAIS_CAT } from '../../constants/accounting';
import { DEVISE, fmt } from '../../lib/currency';
import { demandeDuDossier } from '../../lib/dossier-annee';
import { tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';
import { libellePeriode, moisDesDepenses, OptionsPeriode } from './TicketsA4.jsx';

const CHEMIN = '/depenses/dossier-annee';
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Les dépenses cochées, envoyées d'un geste : une seule ou toutes, en un PDF
 * — le registre, puis chaque ticket et chaque facture telle qu'elle a été
 * reçue. Sur le téléphone, « Envoyer » ouvre le partage (e-mail, WhatsApp,
 * n'importe quelle application) ; à l'ordinateur, le PDF est enregistré pour
 * être joint à un e-mail.
 *
 * @param {object} props propriétés
 * @param {object[]} props.choisies dépenses cochées
 * @param {number} props.nombre dépenses de la liste, pour « Tout cocher »
 * @param {object} [props.soc] entreprise
 * @param {() => void} props.onToutCocher coche ou décoche toute la liste
 * @param {() => void} props.onFermer quitte le choix
 * @returns {import('react').ReactElement} la barre d'envoi
 */
export function EnvoiDepenses({
  choisies,
  nombre,
  soc,
  onToutCocher,
  onFermer,
  frais = [],
  filtre = { periode: 'tout', du: '', au: '', mois: [], type: 'tous' },
  onFiltre = () => {},
  chargeBoite = () => envoisApi.boite(),
  envoieParEmail = (email, dossier, n) => envoisApi.envoieDepenses(email, dossier, n),
}) {
  const [enCours, setEnCours] = useState('');
  const [message, setMessage] = useState('');
  const [adresse, setAdresse] = useState('');
  const [boite, setBoite] = useState(null);

  // La boîte de l'entreprise, si elle est raccordée : l'e-mail part de son
  // adresse (info@…), et la fenêtre le dit.
  useEffect(() => {
    let vivant = true;
    Promise.resolve()
      .then(chargeBoite)
      .then((b) => {
        if (vivant && b?.raccordee) setBoite(b);
      })
      .catch(() => {});
    return () => {
      vivant = false;
    };
    // Une lecture à l'ouverture de la fenêtre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mois = moisDesDepenses(frais);
  const types = Object.keys(FRAIS_CAT).filter((k) => frais.some((x) => x.type === k));
  const change = (champ, valeur) => onFiltre({ ...filtre, [champ]: valeur });
  const total = choisies.reduce((s, x) => s + (+x.montant || 0), 0);
  const rien = choisies.length === 0;
  const tout = nombre > 0 && choisies.length === nombre;
  // « Dépenses · du 01.07.2026 au 31.10.2026 » : la période choisie nomme le PDF.
  const titre =
    choisies.length === 1
      ? choisies[0].libelle || tr('Dépense')
      : filtre.periode !== 'tout'
        ? trf('Dépenses · {0}', libellePeriode(filtre.periode, filtre.du, filtre.au, filtre.mois))
        : tr('Dépenses choisies');
  const nomFichier =
    (titre.replace(/[^\p{L}\p{N} _-]+/gu, '').trim() || 'depenses').replace(/\s+/g, '-') + '.pdf';

  const dossier = () =>
    demandeDuDossier(
      { frais: choisies },
      {
        annee: 'tout',
        resume: choisies.length > 1,
        registre: choisies.length > 1,
        tickets: true,
        mensuelles: true,
        titre,
      },
      soc,
    );

  const lance = async (action) => {
    setMessage('');
    setEnCours(action);
    const demande = dossier();
    try {
      if (action === 'imprimer') {
        await ouvreDocument(CHEMIN, undefined, demande);
        return;
      }
      const fichier = await fichierDocumentDemande(CHEMIN, demande, nomFichier);
      if (action === 'envoyer' && navigator.canShare?.({ files: [fichier] })) {
        try {
          await navigator.share({ files: [fichier], title: titre });
        } catch {
          // Partage annulé : rien d'autre à faire.
        }
        return;
      }
      enregistreFichier(fichier);
      if (action === 'envoyer') {
        setMessage(tr('Le PDF est enregistré : joins-le à ton e-mail.'));
      }
    } catch (e) {
      if (!e?.dejaAffiche) setMessage(e?.message || tr('Le PDF n’a pas pu être créé.'));
    } finally {
      setEnCours('');
    }
  };

  const parEmail = async () => {
    setMessage('');
    setEnCours('email');
    try {
      await envoieParEmail(adresse.trim(), dossier(), choisies.length);
      setMessage(trf('Envoyé à {0}.', adresse.trim()));
    } catch (e) {
      setMessage(e?.message || tr('L’e-mail n’a pas pu partir.'));
    } finally {
      setEnCours('');
    }
  };

  const champ = {
    border: '1px solid ' + C.border,
    borderRadius: 9,
    padding: '9px',
    fontSize: 12.5,
    background: '#fff',
    width: '100%',
    boxSizing: 'border-box',
  };
  const etiquette = { fontSize: 9, fontWeight: 700, color: C.muted, textTransform: 'uppercase' };

  const bouton = (action, texte, plein) => (
    <button
      type="button"
      disabled={rien || !!enCours}
      onClick={() => lance(action)}
      style={{
        flex: 1,
        padding: '10px 4px',
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 800,
        cursor: rien || enCours ? 'default' : 'pointer',
        opacity: rien ? 0.5 : 1,
        border: '1.5px solid ' + C.navy,
        background: plein ? C.navy : '#fff',
        color: plein ? '#fff' : C.navy,
      }}
    >
      {enCours === action ? tr('Préparation…') : texte}
    </button>
  );

  return (
    <div
      data-testid="envoi-depenses"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 5,
        background: C.white,
        border: '2px solid ' + C.navy,
        borderRadius: 12,
        padding: 10,
        marginBottom: 10,
        boxShadow: '0 4px 14px rgba(15,23,42,.12)',
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 800, color: C.navy, marginBottom: 8 }}>
        {tr('📤 Envoyer ou imprimer des dépenses')}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={etiquette}>{tr('Période')}</span>
          <select
            value={filtre.periode}
            onChange={(e) => change('periode', e.target.value)}
            style={champ}
          >
            <OptionsPeriode mois={mois} plusieurs />
          </select>
        </label>
        <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={etiquette}>{tr('Type')}</span>
          <select
            value={filtre.type}
            onChange={(e) => change('type', e.target.value)}
            style={champ}
          >
            <option value="tous">{tr('Tous les types')}</option>
            {types.map((k) => (
              <option key={k} value={k}>
                {FRAIS_CAT[k].icon} {tr(FRAIS_CAT[k].lib)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {filtre.periode === 'libre' && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={etiquette}>{tr('Du')}</span>
            <input
              type="date"
              value={filtre.du}
              max={filtre.au || undefined}
              onChange={(e) => change('du', e.target.value)}
              style={champ}
            />
          </label>
          <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={etiquette}>{tr('Au')}</span>
            <input
              type="date"
              value={filtre.au}
              min={filtre.du || undefined}
              onChange={(e) => change('au', e.target.value)}
              style={champ}
            />
          </label>
        </div>
      )}
      {filtre.periode === 'plusieurs' && (
        // « Plusieurs mois… » : je coche les mois voulus, l'un après l'autre
        // ou non (juillet + septembre).
        <div
          data-testid="plusieurs-mois"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: '4px 10px',
            marginBottom: 8,
          }}
        >
          {mois.map((m) => (
            <label
              key={m}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12.5,
                minHeight: 32,
              }}
            >
              <input
                type="checkbox"
                checked={(filtre.mois || []).includes(m)}
                onChange={() => {
                  const avant = filtre.mois || [];
                  change(
                    'mois',
                    avant.includes(m) ? avant.filter((x) => x !== m) : [...avant, m].sort(),
                  );
                }}
                style={{ width: 18, height: 18 }}
              />
              {libellePeriode(m)}
            </label>
          ))}
        </div>
      )}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}
      >
        <div style={{ flex: 1, fontSize: 12, fontWeight: 800, color: C.navy }}>
          {trf('{0} cochée(s) sur {1}', choisies.length, nombre)}
          {choisies.length > 0 && (
            <span style={{ color: C.muted, fontWeight: 600 }}>
              {' · ' + DEVISE + ' ' + fmt(total)}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onToutCocher}
          style={{
            border: 'none',
            background: C.navyXL,
            color: C.navy,
            borderRadius: 8,
            padding: '6px 9px',
            fontSize: 10.5,
            fontWeight: 800,
            cursor: 'pointer',
          }}
        >
          {tout ? tr('Tout décocher') : tr('Tout cocher')}
        </button>
        <button
          type="button"
          onClick={onFermer}
          style={{
            border: 'none',
            background: 'none',
            color: C.muted,
            fontSize: 10.5,
            fontWeight: 800,
            cursor: 'pointer',
          }}
        >
          {tr('Annuler')}
        </button>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        {bouton('imprimer', '🖨 ' + tr('Imprimer'), true)}
        {bouton('pdf', '📄 ' + tr('PDF'))}
        {bouton('envoyer', '✉️ ' + tr('Envoyer'))}
      </div>
      {!rien && (
        <div style={{ fontSize: 10, color: C.muted, marginTop: 6, lineHeight: 1.4 }}>
          {tr('Un PDF avec chaque ticket et chaque facture telle que reçue.')}
        </div>
      )}
      <div
        style={{
          borderTop: '1px solid ' + C.border,
          marginTop: 8,
          paddingTop: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={etiquette}>{tr('Envoyer par e-mail à')}</span>
          <input
            type="email"
            value={adresse}
            onChange={(e) => setAdresse(e.target.value)}
            placeholder="adresse@exemple.ch"
            autoComplete="email"
            style={champ}
          />
        </label>
        <button
          type="button"
          disabled={rien || !EMAIL.test(adresse.trim()) || !!enCours}
          onClick={parEmail}
          style={{
            padding: '11px 6px',
            borderRadius: 10,
            fontSize: 11.5,
            fontWeight: 800,
            border: 'none',
            background: C.green,
            color: '#fff',
            cursor: 'pointer',
            opacity: rien || !EMAIL.test(adresse.trim()) ? 0.5 : 1,
          }}
        >
          {enCours === 'email'
            ? tr('Envoi…')
            : '📧 ' + trf('Envoyer par e-mail ({0} dépense(s))', choisies.length)}
        </button>
        <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.4 }}>
          {boite?.adresse
            ? trf('Part de ton adresse ({0}), le PDF en pièce jointe.', boite.adresse)
            : tr('Le PDF part en pièce jointe.')}
        </div>
      </div>
      {message && (
        <div style={{ fontSize: 10.5, color: C.navy, fontWeight: 700, marginTop: 6 }}>
          {message}
        </div>
      )}
    </div>
  );
}
